import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useRef, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { verifyOtpForForgotPassword, generateOtp } from '@/api/UserApi';
import { toast } from 'react-toastify';
import { toAbsoluteUrl } from '@/utils';
import { FormattedMessage, useIntl } from 'react-intl';
import BtnSubmitAuth from '../../../../components/ui/btnSubmitAuth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRotateRight, faArrowsRotate } from '@fortawesome/free-solid-svg-icons';

const ResetPasswordEnterCode = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const inputRefs = Array.from({ length: 6 }, () => useRef());
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [countdownInitialized, setCountdownInitialized] = useState(false);

  // Lấy email từ state hoặc query param
  const email = location.state?.email || new URLSearchParams(location.search).get('email');

  const storageKey = email ? `otpResendDeadline:forgotpassword:${email}` : undefined;

  // Initialize countdown from sessionStorage
  useEffect(() => {
    if (!storageKey) return;
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) {
      const deadline = Date.now() + 300 * 1000;
      sessionStorage.setItem(storageKey, String(deadline));
      setRemainingSeconds(300);
      setCountdownInitialized(true);
      return;
    }
    const deadline = Number(raw);
    if (Number.isFinite(deadline)) {
      const diffMs = deadline - Date.now();
      const secs = Math.max(0, Math.floor(diffMs / 1000));
      if (secs > 0) setRemainingSeconds(secs);
      else sessionStorage.removeItem(storageKey);
    }
    setCountdownInitialized(true);
  }, [storageKey]);

  // Countdown timer for resend
  useEffect(() => {
    if (remainingSeconds <= 0) return;
    const interval = setInterval(() => {
      setRemainingSeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [remainingSeconds]);

  // Clean storage when countdown ends (only after initialization)
  useEffect(() => {
    if (!storageKey || !countdownInitialized) return;
    if (remainingSeconds === 0) {
      const raw = sessionStorage.getItem(storageKey);
      const deadline = raw ? Number(raw) : 0;
      if (!deadline || deadline <= Date.now()) {
        sessionStorage.removeItem(storageKey);
      }
    }
  }, [remainingSeconds, storageKey, countdownInitialized]);

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const otpSchema = Yup.object().shape({
    otp: Yup.string()
      .required(intl.formatMessage({ id: 'AUTH.OTP.VALIDATION.REQUIRED' }))
      .matches(/^[0-9]{6}$/, intl.formatMessage({ id: 'AUTH.OTP.VALIDATION.INVALID_FORMAT' }))
  });

  const formik = useFormik({
    initialValues: { otp: '' },
    validationSchema: otpSchema,
    onSubmit: async (values) => {
      setLoading(true);
      formik.setStatus(undefined);
      try {
        const response = await verifyOtpForForgotPassword({ email, otp: values.otp });
        const payload = response && typeof response === 'object' && 'data' in response ? response.data : response;
        const apiStatus = payload && typeof payload.status === 'number' ? payload.status : undefined;
        const apiMessage = payload && payload.message ? payload.message : undefined;

        if (apiStatus === 200 || /verified/i.test(apiMessage || '')) {
          toast.success(intl.formatMessage({ id: 'AUTH.OTP.VERIFIED' }));
          navigate('/auth/reset-password/change', { state: { email, otp: values.otp } });
          return;
        }

        if (apiStatus === 422) {
          formik.setStatus(intl.formatMessage({ id: 'AUTH.ERROR.VALIDATION_FAILED' }));
        } else if (apiStatus === 400) {
          formik.setStatus(
            apiMessage ||
              intl.formatMessage({
                id: 'AUTH.OTP.INVALID_OR_EXPIRED',
                defaultMessage: 'OTP không hợp lệ hoặc đã hết hạn!'
              })
          );
        } else if (typeof apiStatus === 'number') {
          formik.setStatus(apiMessage || intl.formatMessage({ id: 'AUTH.ERROR.SERVER_ERROR' }));
        } else {
          // No apiStatus provided, infer from message
          if (/invalid|expired/i.test(apiMessage || '')) {
            formik.setStatus(
              apiMessage ||
                intl.formatMessage({
                  id: 'AUTH.OTP.INVALID_OR_EXPIRED',
                  defaultMessage: 'OTP không hợp lệ hoặc đã hết hạn!'
                })
            );
          } else {
            formik.setStatus(apiMessage || intl.formatMessage({ id: 'AUTH.ERROR.SERVER_ERROR' }));
          }
        }
      } catch (error) {
        if (!error?.response && (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error' || error?.code === 'ECONNABORTED')) {
          formik.setStatus(intl.formatMessage({ id: 'AUTH.ERROR.NETWORK' }));
        } else if (error?.response) {
          const data = error.response.data || {};
          const status = error.response.status;
          if (status === 422) {
            formik.setStatus(intl.formatMessage({ id: 'AUTH.ERROR.VALIDATION_FAILED' }));
          } else if (status === 400) {
            formik.setStatus(
              data?.message ||
                intl.formatMessage({
                  id: 'AUTH.OTP.INVALID_OR_EXPIRED',
                  defaultMessage: 'OTP không hợp lệ hoặc đã hết hạn!'
                })
            );
          } else {
            formik.setStatus(data?.message || data?.error || intl.formatMessage({ id: 'AUTH.ERROR.SERVER_ERROR' }));
          }
        } else {
          formik.setStatus(error.message || intl.formatMessage({ id: 'AUTH.ERROR.SERVER_ERROR' }));
        }
      } finally {
        setLoading(false);
      }
    }
  });

  // Resend OTP using same logic as ResetPassword
  const handleResend = async (e) => {
    e.preventDefault();
    if (resendLoading || remainingSeconds > 0) return;
    if (!email) {
      formik.setStatus(intl.formatMessage({ id: 'AUTH.ERROR.REQUIRED_EMAIL' }));
      return;
    }
    setResendLoading(true);
    formik.setStatus(undefined);
    try {
      const response = await generateOtp({ email, code_content: 'forgotpassword' });
      if (!response || typeof response.status === 'undefined') {
        formik.setStatus(intl.formatMessage({ id: 'AUTH.ERROR.SERVER_ERROR' }));
        return;
      }
      if (response.status === 200) {
        const msg = (response.message || '').toLowerCase();
        if (msg.includes('still valid')) {
          toast.success(intl.formatMessage({ id: 'AUTH.OTP.STILL_VALID' }));
        } else {
          toast.success(intl.formatMessage({ id: 'AUTH.OTP.SENT' }));
        }
        // Start 5-minute countdown after successful resend
        setRemainingSeconds(300);
        if (storageKey) sessionStorage.setItem(storageKey, String(Date.now() + 300 * 1000));
        return;
      }
      if (response.status === 422) {
        formik.setStatus(intl.formatMessage({ id: 'AUTH.ERROR.INVALID_EMAIL' }));
      } else if (response.status === 429) {
        const secondsMatch = (response.message || '').match(/(\d+)/);
        const seconds = secondsMatch ? Number(secondsMatch[1]) : undefined;
        formik.setStatus(
          seconds !== undefined
            ? intl.formatMessage({ id: 'AUTH.OTP.RATE_LIMIT' }, { seconds })
            : intl.formatMessage({ id: 'AUTH.ERROR.TOO_MANY_REQUESTS' })
        );
        if (seconds !== undefined) {
          setRemainingSeconds(seconds);
          if (storageKey) sessionStorage.setItem(storageKey, String(Date.now() + seconds * 1000));
        }
      } else {
        formik.setStatus(response?.message || response?.error || intl.formatMessage({ id: 'AUTH.ERROR.SERVER_ERROR' }));
      }
    } catch (error) {
      if (!error?.response && (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error' || error?.code === 'ECONNABORTED')) {
        formik.setStatus(intl.formatMessage({ id: 'AUTH.ERROR.NETWORK' }));
      } else {
        formik.setStatus(error.message || intl.formatMessage({ id: 'AUTH.ERROR.SERVER_ERROR' }));
      }
    } finally {
      setResendLoading(false);
    }
  };

  // Xử lý nhập từng ô
  const handleInputChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return;
    const otpArr = formik.values.otp.split('');
    otpArr[index] = value;
    const newOtp = otpArr.join('').padEnd(6, '');
    formik.setFieldValue('otp', newOtp);
    if (value && index < 5) {
      inputRefs[index + 1].current?.focus();
    }
    if (!value && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  // Xử lý paste 6 số vào input đầu tiên
  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '');
    if (paste.length === 6) {
      formik.setFieldValue('otp', paste);
      setTimeout(() => {
        inputRefs[5].current?.focus();
      }, 0);
      e.preventDefault();
    }
  };

  return (
    <div className="card max-w-[380px] w-full">
      <form className="card-body flex flex-col gap-3 p-5 md:p-10" onSubmit={formik.handleSubmit}>
        <img src={toAbsoluteUrl('/media/illustrations/30.svg')} className="dark:hidden max-h-[130px]" alt="" />
        <img src={toAbsoluteUrl('/media/illustrations/30-dark.svg')} className="light:hidden max-h-[130px]" alt="" />

        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            <FormattedMessage id="AUTH.OTP.ENTER_CODE_TITLE" />
          </h3>
          <div className="flex flex-col">
            <span className="text-2sm text-gray-700">
              <FormattedMessage id="AUTH.OTP.ENTER_CODE_DESC" />
            </span>
          </div>
        </div>

        {/* Reset button */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => {
              formik.resetForm();
              formik.setStatus(undefined);
              // Focus vào input đầu tiên
              inputRefs[0].current?.focus();
            }}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            <FontAwesomeIcon icon={faArrowsRotate} className='size-4'/>
          </button>
        </div>

        <div className="grid grid-cols-6 gap-1 sm:gap-2 md:gap-2.5 justify-center">
          {[...Array(6)].map((_, index) => (
            <input
              key={index}
              ref={inputRefs[index]}
              id={`otp-input-${index}`}
              type="text"
              maxLength={1}
              className="input focus:border-primary-clarity focus:ring focus:ring-primary-clarity size-8 sm:size-10 shrink-0 px-0 text-center text-sm sm:text-base"
              value={formik.values.otp[index] || ''}
              onChange={e => handleInputChange(index, e.target.value)}
              onFocus={e => e.target.select()}
              autoFocus={index === 0}
              onPaste={index === 0 ? handlePaste : undefined}
            />
          ))}
        </div>
        {formik.touched.otp && formik.errors.otp && (
          <div className="text-danger text-xs text-center mt-2">{formik.errors.otp}</div>
        )}
        {formik.status && (
          <div className="text-red-500 text-sm text-center bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {formik.status}
          </div>
        )}

        <BtnSubmitAuth
          loading={loading || formik.isSubmitting}
          disabled={loading || formik.isSubmitting}
        >
          {loading || formik.isSubmitting ? (
            <FormattedMessage id="AUTH.PLEASE_WAIT" />
          ) : (
            <FormattedMessage id="AUTH.OTP.CONFIRM" />
          )}
        </BtnSubmitAuth>

        <div className="flex items-center justify-center mb-2 gap-1.5">
          <span className="text-xs text-gray-700">
            <FormattedMessage id="AUTH.OTP.NOT_RECEIVE" />
          </span>
          {remainingSeconds > 0 ? (
            <span className="text-xs text-gray-500 font-medium">{formatTime(remainingSeconds)}</span>
          ) : (
            <a
              href="#"
              onClick={handleResend}
              className={
                'text-xs link ' + (resendLoading ? 'pointer-events-none opacity-60 text-gray-400' : 'text-[#4AA642]')
              }
            >
              <FormattedMessage id="AUTH.OTP.RESEND" />
            </a>
          )}
        </div>
      </form>
    </div>
  );
};
export { ResetPasswordEnterCode };